import React from 'react';
import {NavLink} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {fedelta as leggiFedelta} from '../../services/casinoApi';
import {paginaDelMenu, paginaScritta} from '../../moduli/leggi';
import {getCurrentUserId, isPlayer} from '../../utils/currentUser';
import {lingua} from '../../preferenze';
import Nota from '../Vetrina/Nota';
import IconaNova from '../Nova/IconaNova';

/**
 * IL PROGRAMMA FEDELTA' VERO SUL SITO (passo 4 del piano, 23 ottobre): i
 * punti e il livello del giocatore, i livelli coi loro premi e come si fanno
 * i punti, tutto da casino-service (services/casinoApi.js, fedelta). Il
 * programma si scrive nel backoffice (Bonus marketing › Crea nuovo bonus ›
 * Programma fedelta'); qui si legge e basta.
 *
 * Si vede col modulo Pagine › Programma fedelta' acceso e il suo interruttore
 * «Punti e livello del giocatore» acceso: la pagina /fedelta, il livello in
 * testata e nella colonna del conto. Se il servizio non risponde resta lo
 * scheletro; senza programma, ai giocatori non si mostra niente.
 */

/* Le sezioni coi pesi, coi nomi del sito (chiavi di casino-service). */
const sezioni = (t) => ({casino: t('Casinò'), liveCasino: t('Live casino'), virtualiCasino: t('Virtual'), crashGame: t('Crash game')});
const SIMBOLI = {EUR: '€', USD: '$', GBP: '£'};

/** Il programma fedelta' si mostra: pagina accesa e punti del giocatore accesi. */
export function fedeltaInMostra() {
  const pagina = paginaDelMenu('fedelta');
  return !!(pagina && pagina.acceso && paginaScritta('fedelta').backend);
}

/**
 * Il programma e i punti di chi guarda: {stato: 'carica' | 'pronto' | 'errore', dati}.
 * Una richiesta sola per testata, colonna e pagina (casinoApi la tiene 30 s);
 * dopo una puntata o un versamento si rilegge. `attivo` falso: non chiede niente.
 */
export function useFedelta(attivo = true) {
  const chi = getCurrentUserId() || '';
  const [esito, setEsito] = React.useState({stato: 'carica', dati: null});
  React.useEffect(() => {
    if (!attivo) return undefined;
    let vivo = true;
    const leggi = (fresco) => leggiFedelta(fresco)
        .then((dati) => { if (vivo) setEsito({stato: 'pronto', dati}); })
        .catch(() => { if (vivo) setEsito((e) => (e.stato === 'pronto' ? e : {stato: 'errore', dati: null})); });
    const rileggi = () => leggi(true);
    leggi(false);
    window.addEventListener('balanceUpdated', rileggi);
    window.addEventListener('giocataCambiata', rileggi);
    return () => {
      vivo = false;
      window.removeEventListener('balanceUpdated', rileggi);
      window.removeEventListener('giocataCambiata', rileggi);
    };
  }, [attivo, chi]);
  return esito;
}

/* Il separatore delle migliaia anche sotto i diecimila («1.240», non «1240» come vorrebbe l'italiano). */
const MIGLIAIA = {useGrouping: 'always'};

/** I punti come numero intero: «1.240». */
export function puntiTesto(n) {
  return Math.floor(Number(n) || 0).toLocaleString(lingua(), MIGLIAIA);
}

/** «10 €», «0,50 €»: i decimali solo se ci sono. */
export function soldi(n, valuta) {
  const v = String(valuta || '').toUpperCase();
  const testo = (Number(n) || 0).toLocaleString(lingua(), {...MIGLIAIA, minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2});
  return v ? `${testo} ${SIMBOLI[v] || v}` : testo;
}

/** «21/01/2027» da AAAA-MM-GG o da un istante. */
function giorno(s) {
  if (!s) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T00:00:00' : s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(lingua(), {day: '2-digit', month: '2-digit', year: 'numeric'});
}

function oggi() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** Perche' il programma non e' in corso: 'parte', 'finito' o 'spento' (o null se e' in corso). */
function fuoriDalleDate(p) {
  if (p.inCorso) return null;
  if (p.validoDal && p.validoDal > oggi()) return 'parte';
  if (p.validoAl && p.validoAl < oggi()) return 'finito';
  return 'spento';
}

/**
 * IL RIQUADRO «I TUOI PUNTI», al posto dello scheletro: livello, punti, barra
 * verso il prossimo livello, il rakeback della settimana finora e quando
 * scadono i punti. All'ospite: entra per vederli. `scheletro` torna mentre
 * carica e se il servizio non risponde.
 */
export function ITuoiPunti({scheletro, userService}) {
  const {t} = useTranslation();
  const {stato, dati} = useFedelta();
  if (stato !== 'pronto') return scheletro;
  if (!dati.programma) {
    return (
      <Nota tono={'spento'} piccola>
        {t('Questa skin non ha ancora un programma fedeltà: si crea nel backoffice, Bonus marketing › Crea nuovo bonus › Programma fedeltà.')}
      </Nota>
    );
  }
  const p = dati.programma;
  const fuori = fuoriDalleDate(p);
  if (fuori === 'spento') {
    return <Nota tono={'spento'} piccola>{t('Il programma fedeltà è spento nel backoffice: ai giocatori non si mostra.')}</Nota>;
  }
  const avviso = fuori === 'parte'
    ? t('Il programma parte il {{giorno}}.', {giorno: giorno(p.validoDal)})
    : fuori === 'finito' ? t('Il programma è finito il {{giorno}}.', {giorno: giorno(p.validoAl)}) : '';
  if (dati.ospite) {
    const entra = userService && typeof userService.richiediAccesso === 'function';
    return (
      <div className={'pg-scheletro pg-posizione pg-fedelta'} aria-label={t('I tuoi punti')}>
        <strong>{t('I tuoi punti')}</strong>
        {avviso && <span className={'pg-fedelta-avviso'}>{avviso}</span>}
        <span>{t('Entra nel tuo conto per vedere i tuoi punti e il tuo livello.')}</span>
        {entra && <button type={'button'} className={'pg-oro pg-fedelta-entra'} onClick={() => userService.richiediAccesso()}>{t('Accedi')}</button>}
      </div>
    );
  }
  if (dati.escluso) {
    return (
      <div className={'pg-scheletro pg-posizione pg-fedelta'} aria-label={t('I tuoi punti')}>
        <strong>{t('I tuoi punti')}</strong>
        <span>{t('Il tuo conto non partecipa al programma fedeltà. Per sapere perché scrivi all’assistenza.')}</span>
      </div>
    );
  }
  const {livello, prossimo} = dati;
  const da = livello ? livello.soglia : 0;
  const quota = prossimo && prossimo.soglia > da ? Math.min(100, Math.max(0, ((dati.punti - da) / (prossimo.soglia - da)) * 100)) : 100;
  const livelloOra = livello && p.livelli[livello.indice];
  const primoRakeback = p.livelli.find((l) => l.rakeback > 0);
  const rakeback = dati.settimana.filter((v) => v.rakeback > 0);
  return (
    <div className={'pg-scheletro pg-posizione pg-fedelta'} aria-label={t('I tuoi punti')}>
      <span className={'pg-fedelta-testa'}>
        <strong>{t('I tuoi punti')}</strong>
        {livello && <span className={'pg-fedelta-livello'}><IconaNova nome={'stella'} misura={14}/>{livello.nome}</span>}
      </span>
      {avviso && <span className={'pg-fedelta-avviso'}>{avviso}</span>}
      <span className={'pg-posizione-numero'}>{puntiTesto(dati.punti)}</span>
      <span className={'pg-posizione-barra'} role={'progressbar'} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(quota)}
        aria-label={prossimo ? t('Verso il livello {{livello}}', {livello: prossimo.nome}) : t('Livello più alto')}>
        <span style={{width: quota + '%'}}/>
      </span>
      <span className={'pg-fedelta-riga'}>
        {prossimo
          ? t('Mancano {{punti}} punti per {{livello}}.', {punti: puntiTesto(Math.ceil(prossimo.mancano)), livello: prossimo.nome})
          : t('Sei al livello più alto.')}
      </span>
      {rakeback.map((v) => (
        <span key={v.valuta} className={'pg-fedelta-riga'}>
          {t('Rakeback di questa settimana finora: {{importo}}. Arriva lunedì nel saldo bonus.', {importo: soldi(v.rakeback, v.valuta)})}
        </span>
      ))}
      {!rakeback.length && livelloOra && !(livelloOra.rakeback > 0) && primoRakeback && (
        <span className={'pg-fedelta-riga'}>{t('Il rakeback settimanale parte dal livello {{livello}}.', {livello: primoRakeback.nome})}</span>
      )}
      {dati.scadono && (
        <span className={'pg-fedelta-riga piccola'}>{t('Se non giochi, i punti tornano a zero il {{giorno}}.', {giorno: giorno(dati.scadono)})}</span>
      )}
    </div>
  );
}

/**
 * I LIVELLI DEL PROGRAMMA, sotto l'apertura: come si fanno i punti e la
 * tabella coi livelli veri (da quanti punti, premio di salita, rakeback),
 * col livello del giocatore segnato. Niente se il programma non c'e' o e' spento.
 */
export function LivelliFedelta() {
  const {t} = useTranslation();
  const {stato, dati} = useFedelta();
  if (stato !== 'pronto' || !dati.programma || fuoriDalleDate(dati.programma) === 'spento') return null;
  const p = dati.programma;
  const mio = !dati.ospite && dati.livello ? dati.livello.indice : -1;
  const premio = (l) => Object.entries(l.premioSalita)
      .filter(([, v]) => Number(v) > 0)
      .map(([valuta, v]) => soldi(v, valuta))
      .join(' · ');
  const conPremio = p.livelli.some((l) => premio(l));
  const conRakeback = p.livelli.some((l) => l.rakeback > 0);
  const regole = [];
  Object.entries(p.tassi).forEach(([valuta, v]) => {
    if (Number(v) > 0) regole.push(t('1 punto ogni {{importo}} giocati.', {importo: soldi(v, valuta)}));
  });
  Object.entries(sezioni(t)).forEach(([chiave, nome]) => {
    const peso = p.pesi[chiave];
    if (peso === undefined || peso === null || Number(peso) === 100) return;
    regole.push(Number(peso) <= 0
      ? t('{{sezione}}: non dà punti.', {sezione: nome})
      : t('{{sezione}}: conta al {{peso}}%.', {sezione: nome, peso: Number(peso)}));
  });
  if (conPremio || conRakeback) regole.push(t('I premi arrivano nel saldo bonus: quello di salita una volta per livello, il rakeback ogni lunedì sul giocato della settimana.'));
  if (p.scadenzaGiorni) regole.push(t('I punti tornano a zero dopo {{n}} giorni senza giocare.', {n: p.scadenzaGiorni}));
  return (
    <section className={'pg-corpo pg-fedelta-livelli'} aria-label={t('I livelli')}>
      <h2 className={'pg-titolo'}>{t('I livelli')}</h2>
      {regole.length > 0 && <ul className={'pg-fedelta-regole'}>{regole.map((r) => <li key={r}>{r}</li>)}</ul>}
      <div className={'pg-tabella-scorri'}>
        <table className={'pg-livelli'}>
          <thead>
            <tr>
              <th scope={'col'}>{t('Livello')}</th>
              <th scope={'col'}>{t('Da punti')}</th>
              {conPremio && <th scope={'col'}>{t('Premio di salita')}</th>}
              {conRakeback && <th scope={'col'}>{t('Rakeback settimanale')}</th>}
            </tr>
          </thead>
          <tbody>
            {p.livelli.map((l, i) => (
              <tr key={l.nome + i} className={i === mio ? 'pg-fedelta-mio' : undefined} aria-current={i === mio ? 'true' : undefined}>
                <th scope={'row'}>
                  <span className={'pg-livello-gemma'} aria-hidden={'true'} style={{'--i': i}}/>{l.nome}
                  {i === mio && <span className={'pg-fedelta-qui'}>{t('Sei qui')}</span>}
                </th>
                <td>{puntiTesto(l.soglia)}</td>
                {conPremio && <td className={'pg-col-cambio'}>{premio(l) || '—'}</td>}
                {conRakeback && (
                  <td className={'pg-col-cashback'}>
                    {l.rakeback > 0 ? l.rakeback.toLocaleString(lingua(), {maximumFractionDigits: 2}) + '%' : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * IL LIVELLO IN TESTATA: la stella e il nome del livello, che porta a
 * /fedelta. Solo a chi gioca, col programma in mostra, in corso o finito
 * (non spento), e se il giocatore non e' escluso.
 */
export function LivelloInTestata({classe}) {
  const {t} = useTranslation();
  const mostra = isPlayer() && fedeltaInMostra();
  const {stato, dati} = useFedelta(mostra);
  const livello = mostra && stato === 'pronto' ? livelloDelGiocatore(dati) : null;
  if (!livello) return null;
  return (
    <NavLink to={'/fedelta'} className={'nova-livello' + (classe ? ' ' + classe : '')}
      title={t('Programma fedeltà: livello {{livello}}, {{punti}} punti', {livello: livello.nome, punti: puntiTesto(dati.punti)})}
      aria-label={livello.nome}>
      <IconaNova nome={'stella'} misura={16}/><span>{livello.nome}</span>
    </NavLink>
  );
}

/** Il livello da mostrare (testata, colonna del conto), o null. */
export function livelloDelGiocatore(dati) {
  if (!dati || dati.ospite || dati.escluso || !dati.programma || !dati.livello) return null;
  return fuoriDalleDate(dati.programma) === 'spento' ? null : dati.livello;
}
